import {
  Box,
  Collapse,
  IconButton,
  Paper,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { KeyboardEvent, ReactNode, useState } from "react";
import { cardSx, tokens } from "../tokens";

interface Props {
  title?: string;
  subtitle?: string;
  /** Rendered on the right of the header row (filters, actions, …). */
  headerAction?: ReactNode;
  /** Removes the body padding so a card can host a full-bleed list. */
  disablePadding?: boolean;
  /** Defaults to `disablePadding`; set false to drop the header divider. */
  headerDivider?: boolean;
  /**
   * Adds a chevron to the header and makes the whole header row clickable to
   * expand or collapse the body.
   */
  collapsible?: boolean;
  defaultExpanded?: boolean;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

/** The surface every section of the redesigned Edit screen sits on. */
const SectionCard = ({
  title,
  subtitle,
  headerAction,
  disablePadding = false,
  headerDivider,
  collapsible = false,
  defaultExpanded = true,
  sx,
  children,
}: Props) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const open = collapsible ? expanded : true;

  const toggle = () => setExpanded((isOpen) => !isOpen);

  // Only divide the header from a body that is actually showing.
  const showDivider = (headerDivider ?? disablePadding) && open;

  // A collapsed card would otherwise end flush against the header text.
  const headerPadding = disablePadding
    ? "18px 22px"
    : open
      ? "22px 22px 0"
      : "22px";

  const body = (
    <Box sx={{ padding: disablePadding ? 0 : "16px 22px 22px" }}>
      {children}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      {(title || headerAction || collapsible) && (
        <Box
          {...(collapsible && {
            role: "button",
            tabIndex: 0,
            "aria-expanded": open,
            "aria-label": `${open ? "Collapse" : "Expand"} ${title ?? "section"}`,
            onClick: toggle,
            onKeyDown: (event: KeyboardEvent) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggle();
              }
            },
          })}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            padding: headerPadding,
            borderBottom: showDivider ? `1px solid ${tokens.border}` : "none",
            /* When collapsed the header is the whole card, so let it absorb
               any extra height the surrounding row gives it. */
            ...(collapsible && !open && { flex: 1 }),
            ...(collapsible && {
              cursor: "pointer",
              userSelect: "none",
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: tokens.bg },
              "&:focus-visible": {
                outline: `2px solid ${tokens.accent}`,
                outlineOffset: "-2px",
              },
            }),
          }}
        >
          <Box>
            {title && (
              <Typography
                component="h2"
                sx={{ fontSize: 15, fontWeight: 700, color: tokens.text }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                sx={{ fontSize: 12, color: tokens.textFaint, mt: "2px" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {headerAction}
            {collapsible && (
              /* Decorative: the header row itself carries the interaction. */
              <IconButton
                aria-hidden
                tabIndex={-1}
                size="small"
                sx={{
                  pointerEvents: "none",
                  color: tokens.textMuted,
                  transition: "transform 0.2s ease",
                  transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {collapsible ? (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {body}
        </Collapse>
      ) : (
        body
      )}
    </Paper>
  );
};

export default SectionCard;
